import React, { useMemo, useState, useEffect, useRef } from 'react';
import { TreeSelect } from 'antd';

/*
  Expects facets like:
  [{ value: "/GLOBAL/ALL ENTITIES/Belux, ...", count: 1 }, ...]
  or options can be an object containing a property with that array.
*/

const StaticTreeMultiSelect = ({
  options = [],
  facets = [],
  value = [],
  onChange,
  placeholder = 'Select ',
  disabled = false,
  maxTagTextLength = 20,
  maxTagCount = 1,
  // 'regions' (default) | 'commodities' | 'auto'
  dataType = 'regions',
}) => {

  // Normalize incoming data to an array of facet objects
  const input = useMemo(() => {
    if (Array.isArray(facets) && facets.length) return facets;
    if (Array.isArray(options) && options.length) return options;

    // If options or facets are objects that contain the array under a property
    const maybeArrayFromObj = (obj) => {
      if (!obj || typeof obj !== 'object') return null;
      // prefer by requested dataType: when rendering regions, prefer regions
      if (dataType === 'regions') {
        if (Array.isArray(obj.regions)) return obj.regions;
        if (Array.isArray(obj['regions/name'])) return obj['regions/name'];
        if (Array.isArray(obj.HierarchyPath_Description)) return obj.HierarchyPath_Description;
      }
      if (dataType === 'commodities') {
        if (Array.isArray(obj.commodities)) return obj.commodities;
        if (Array.isArray(obj['commodities/name'])) return obj['commodities/name'];
        // some responses place commodity paths under HierarchyPath_Description
        if (Array.isArray(obj.HierarchyPath_Description)) return obj.HierarchyPath_Description;
        if (Array.isArray(obj.categories)) return obj.categories;
        if (Array.isArray(obj['categories/name'])) return obj['categories/name'];
      }
      // fallback ordering (regions first to preserve existing region behavior)
      if (Array.isArray(obj.regions)) return obj.regions;
      if (Array.isArray(obj.commodities)) return obj.commodities;
      // common facet shapes
      if (Array.isArray(obj.HierarchyPath_Description)) return obj.HierarchyPath_Description;
      if (Array.isArray(obj.facets)) return obj.facets;
      // shapes like { value: ["/a","/b"] }
      if (Array.isArray(obj.value) && obj.value.length && typeof obj.value[0] === 'string') {
        return obj.value.map(v => ({ value: v }));
      }
      // some APIs return data nested under selectScope
      if (obj.selectScope && typeof obj.selectScope === 'object') {
        if (dataType === 'commodities') {
          if (Array.isArray(obj.selectScope.commodities)) return obj.selectScope.commodities;
          if (Array.isArray(obj.selectScope.categories)) return obj.selectScope.categories;
        } else {
          if (Array.isArray(obj.selectScope.results)) return obj.selectScope.results;
          if (Array.isArray(obj.selectScope.regions)) return obj.selectScope.regions;
        }
      }
      // keys that include commodity/category or region in unexpected shapes
      for (const k of Object.keys(obj)) {
        if (!Array.isArray(obj[k]) || !obj[k].length) continue;
        const lk = String(k).toLowerCase();
        if (dataType === 'commodities' && (lk.includes('commodit') || lk.includes('category'))) return obj[k];
        if (dataType === 'regions' && (lk.includes('region'))) return obj[k];
      }
      // fallback: return first array-like property (useful for unexpected facet shapes)
      for (const k of Object.keys(obj)) {
        if (Array.isArray(obj[k]) && obj[k].length) return obj[k];
      }
      return null;
    };

    const fromOptionsObj = maybeArrayFromObj(options);
    if (fromOptionsObj) return fromOptionsObj;
    const fromFacetsObj = maybeArrayFromObj(facets);
    if (fromFacetsObj) return fromFacetsObj;

    return options;
  }, [options, facets, dataType]);

  // When the incoming array may contain both regions and commodities, filter it by requested dataType
  const filteredInput = useMemo(() => {
    if (!Array.isArray(input)) return input;
    if (dataType === 'auto') return input;

    const normalizeRaw = (it) => {
      const raw = (typeof it === 'string') ? it : (it?.value || it?.title || it?.name || '');
      return String(raw || '').trim();
    };

    const isGenericAll = (raw) => {
      if (!raw) return false;
      // only treat plain "All" or "Not Assigned" as generic (do NOT match "All Entities" inside a path)
      return /^\s*all\s*$/i.test(raw) || /^not assigned$/i.test(raw);
    };

    const isCommodityLike = (raw) => {
      if (!raw) return false;
      // obvious commodity path uses '|' separators
      if (raw.includes('|')) return true;
      // contains commodity/category words
      if (/commodit|category/i.test(raw)) return true;
      // contains B-code like B172400 or B 172400 (common commodity id)
      if (/\bB\s*\d{3,}\b/i.test(raw)) return true;
      return false;
    };

    if (dataType === 'commodities') {
      return input.filter(it => {
        const raw = normalizeRaw(it);
        if (!raw) return false;
        // keep "All Commodities" explicitly
        if (/^\s*all\s+commodit/i.test(raw)) return true;
        // drop generic "All", "Not Assigned", "All Entities"
        if (isGenericAll(raw)) return false;
        // drop clear region-like global entities
        if (/global/i.test(raw) && /entities/i.test(raw)) return false;
        // keep commodity-like entries
        if (isCommodityLike(raw)) return true;
        // drop slash-separated region paths (unless they contain commodity hints)
        if (raw.includes('/') && !isCommodityLike(raw)) return false;
        // defensive: keep only non-empty strings
        return raw.length > 0;
      });
    }
    if (dataType === 'regions') {
      // exclude commodity/category named items (e.g. "All Commodities") so they don't show up in Scope
      return input.filter(it => {
        const raw = normalizeRaw(it);
        if (!raw) return false;
        // drop only plain "All" or "Not Assigned" (keep "/GLOBAL/ALL ENTITIES/..." paths)
        if (isGenericAll(raw)) return false;
        if (/commodit|category/i.test(raw)) return false;
        return raw.includes('/') || !raw.includes('|');
      });
    }
    return input;
  }, [input, dataType]);

  // build nested Map tree from path strings (preserve leading slash in values)
  // ---- Performance: lazy tree building ----
  // Build a fast "flat" node map synchronously so normalization / labels are instant.
  const flatNodes = useMemo(() => {
    const map = new Map(); // value -> { title, value, fullPath, parent }
    (filteredInput || []).forEach(item => {
      if (!item) return;
      const rawVal = (typeof item === 'string') ? item : (item.value || '');
      if (!rawVal) return;
      const cleaned = String(rawVal).replace(/^\/+/, '');
      if (!cleaned) return;
      const parts = cleaned.split('|').map(p => p.trim()).filter(Boolean);
      let pathSoFar = '';
      for (let i = 0; i < parts.length; i++) {
        pathSoFar = pathSoFar ? `${pathSoFar}|${parts[i]}` : `${parts[i]}`;
        const withSlash = `/${pathSoFar}`;
        const title = parts[i];
        const parentPath = i === 0 ? null : `/${pathSoFar.split('|').slice(0, -1).join('|')}`;
        if (!map.has(withSlash)) {
          map.set(withSlash, { title, value: withSlash, fullPath: withSlash, parentPath });
        }
      }
    });
    return map;
  }, [filteredInput]);

  // top-level shallow tree (fast) - show only root level nodes initially
  const shallowTreeData = useMemo(() => {
    const top = [];
    flatNodes.forEach((v) => {
      if (!v.parentPath) {
        const label = v.fullPath.replace(/^\/+/, '').split('|').map(s => s.trim()).join(' / ');
        top.push({ title: v.title, label, value: v.value, key: v.value, fullPath: v.fullPath, children: [] });
      }
    });
    // sort for consistent UI
    top.sort((a, b) => String(a.title).localeCompare(String(b.title)));
    return top;
  }, [flatNodes]);

  // Default expand: first level only (top-level nodes)
  const topLevelKeys = useMemo(() => {
    return (shallowTreeData || []).map(n => n?.key || n?.value).filter(Boolean);
  }, [shallowTreeData]);

  // state to control dropdown visibility and whether full tree built
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [fullTreeReady, setFullTreeReady] = useState(false);
  const [treeDataState, setTreeDataState] = useState(shallowTreeData);
  const buildRequestedRef = useRef(false);

  // Keep the search input controlled so it doesn't get cleared when a user selects an item
  const [searchValue, setSearchValue] = useState('');

  // Build the full nested tree asynchronously when dropdown opens.
  useEffect(() => {
    // reset if input changes
    setFullTreeReady(false);
    setTreeDataState(shallowTreeData);
    buildRequestedRef.current = false;
  }, [shallowTreeData, filteredInput]);

  useEffect(() => {
    if (!dropdownOpen || fullTreeReady || buildRequestedRef.current) return;
    buildRequestedRef.current = true;

    const doBuild = () => {
      // build nested Map -> array (same logic as before, but runs asynchronously)
      const root = new Map();
      flatNodes.forEach(n => {
        const cleaned = String(n.fullPath).replace(/^\/+/, '');
        const parts = cleaned.split('|').map(p => p.trim()).filter(Boolean);
        let currentMap = root;
        let pathSoFar = '';
        parts.forEach((part) => {
          pathSoFar = pathSoFar ? `${pathSoFar}|${part}` : `${part}`;
          const withSlash = `/${pathSoFar}`;
          if (!currentMap.has(part)) {
            currentMap.set(part, {
              title: part,
              value: withSlash,
              key: withSlash,
              fullPath: withSlash,
              children: new Map()
            });
          }
          const node = currentMap.get(part);
          currentMap = node.children;
        });
      });

      const mapToArray = (map) => {
        return Array.from(map.values()).map(n => {
          const children = mapToArray(n.children);
          const fullLabel = n.fullPath
            ? n.fullPath.replace(/^\/+/, '').split('|').map(s => s.trim()).join(' / ')
            : n.title;
          const out = { title: n.title, label: fullLabel, value: n.value, key: n.key };
          if (n.fullPath) out.fullPath = n.fullPath;
          if (children.length) out.children = children;
          return out;
        });
      };

      const full = mapToArray(root);
      setTreeDataState(full);
      setFullTreeReady(true);
    };

    // schedule low-priority work
    if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
      (window).requestIdleCallback(doBuild, { timeout: 2000 });
    } else {
      setTimeout(doBuild, 0);
    }
  }, [dropdownOpen, flatNodes, fullTreeReady]);

  // map value -> full label (used for tag rendering)
  // Use flatNodes (fast) to build label map instantly
  const labelByValue = useMemo(() => {
    const map = new Map();
    flatNodes.forEach((n, v) => {
      const label = (n.fullPath || n.value || '').replace(/^\/+/, '').split('|').map(s => s.trim()).join(' / ');
      map.set(v, label || n.title || String(v));
    });
    return map;
  }, [flatNodes]);

  // helper: map of regionName(lowercase) -> [fullPath, ...] for normalization
  // Build pathsByRegion from flatNodes (fast)
  const pathsByRegion = useMemo(() => {
    const map = new Map();
    flatNodes.forEach((n) => {
      const regionKey = String(n.title || '').trim().toLowerCase();
      const path = n.fullPath || n.value;
      if (path) {
        if (!map.has(regionKey)) map.set(regionKey, []);
        map.get(regionKey).push(path);
      }
    });
    return map;
  }, [flatNodes]);

  // normalize incoming value array to node values (leading slash form)
  const normalizedValue = useMemo(() => {
    if (!Array.isArray(value)) return [];
    const out = [];
    value.forEach(v => {
      if (typeof v !== 'string') return;
      const s = v.trim();
      if (s.startsWith('/')) {
        // already a fullPath
        out.push(s);
      } else {
        // treat as region-only name -> map to all matching fullPaths
        const matches = pathsByRegion.get(s.toLowerCase());
        if (matches && matches.length) out.push(...matches);
      }
    });
    // unique
    return Array.from(new Set(out));
  }, [value, pathsByRegion]);

  // handle dropdown open/close; used for lazy-building tree
  const handleDropdownVisibleChange = (open) => {
    setDropdownOpen(open);
  };

  // emit selection as array of facet-format strings (leading slash)
  const handleChange = (vals) => {
    const arr = Array.isArray(vals) ? vals : vals ? [vals] : [];
    const selectedPaths = arr.map(v => (typeof v === 'string' ? (v.startsWith('/') ? v : `/${v.replace(/^\/+/, '')}`) : null)).filter(Boolean);
    const uniquePaths = Array.from(new Set(selectedPaths));
    const selSet = new Set(uniquePaths);

    const gatherLeafPaths = (node) => {
      if (!node) return [];
      if (!Array.isArray(node.children) || node.children.length === 0) {
        return [node.value || node.fullPath].filter(Boolean);
      }
      return node.children.reduce((acc, child) => acc.concat(gatherLeafPaths(child)), []);
    };

    // find a node in the built tree by its value
    const findNodeByValue = (nodes, value) => {
      if (!Array.isArray(nodes)) return null;
      for (const n of nodes) {
        if ((n.value || n.fullPath) === value) return n;
        if (n.children && n.children.length) {
          const found = findNodeByValue(n.children, value);
          if (found) return found;
        }
      }
      return null;
    };

    const collectKeep = (nodes) => {
      const out = [];
      nodes.forEach(n => {
        const nodeVal = n.value || n.fullPath;
        if (nodeVal && selSet.has(nodeVal)) {
          out.push(nodeVal);
          return;
        }
        const leaves = gatherLeafPaths(n);
        if (leaves.length && leaves.every(lp => selSet.has(lp))) {
          out.push(nodeVal);
        } else if (Array.isArray(n.children) && n.children.length) {
          out.push(...collectKeep(n.children));
        } else {
          if (nodeVal && selSet.has(nodeVal)) out.push(nodeVal);
        }
      });
      return out;
    };

    const kept = Array.from(new Set(collectKeep(treeDataState)));

    // Resolve each kept node to its leaf-level paths (so parent -> all its leaves)
    const leafPaths = new Set();
    kept.forEach(k => {
      const node = findNodeByValue(treeDataState, k);
      if (node) {
        gatherLeafPaths(node).forEach(p => leafPaths.add(p));
      } else {
        // fallback: treat kept value as leaf path
        leafPaths.add(k);
      }
    });

    // Map leaf full-paths to lowest-level labels (last segment)
    const out = Array.from(leafPaths).map(p => {
      const segs = (p || '').replace(/^\/+/, '').split('|').filter(Boolean);
      return segs.length ? segs[segs.length - 1].trim() : '';
    }).filter(Boolean);

    onChange && onChange(Array.from(new Set(out)));
  };

  return (
    <div style={{ minWidth: 200 }}>
      <TreeSelect
        treeData={treeDataState}
        treeCheckable
        searchValue={searchValue}
        onSearch={(val) => setSearchValue(val)}
        autoClearSearchValue={false}
        dropdownStyle={{ width: 400 }}
        dropdownMatchSelectWidth={false}
        showCheckedStrategy={TreeSelect.SHOW_ALL}
        value={normalizedValue}
        onChange={handleChange}
        onDropdownVisibleChange={handleDropdownVisibleChange}
        placeholder={placeholder}
        maxTagTextLength={maxTagTextLength}
        maxTagCount={maxTagCount}
        allowClear
        disabled={disabled}
        style={{ minWidth: '200px', marginLeft: '10px' }}
        getPopupContainer={() => document.body}
        showSearch
        filterTreeNode={(inputText, node) => {
          const hay = `${String(node.title || '')} ${String(node.label || '')}`.toLowerCase();
          return hay.includes((inputText || '').toLowerCase());
        }}
        tagRender={(props) => {
          const { label, value, closable, onClose } = props;
          let labelText =
            (typeof label === 'string' && label) ||
            labelByValue.get(value) ||
            String(value);

          if (typeof labelText === 'string' && labelText.length > maxTagTextLength) {
            labelText = `${labelText.slice(0, maxTagTextLength - 1)}…`;
          }

          return (
            <span className="ant-select-selection-item" style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{labelText}</span>
              {closable && (
                <span style={{ cursor: 'pointer' }} onClick={onClose}>
                  ×
                </span>
              )}
            </span>
          );
        }}
        className="select"
        treeDefaultExpandAll={false}
        treeDefaultExpandedKeys={topLevelKeys}
      />
    </div>
  );
};

export default StaticTreeMultiSelect;